import { useState, useEffect, useCallback, useRef } from 'react';
import { ENABLED_COUNTRIES } from '../constants/countries';

/**
 * Hook to detect user's country via browser geolocation + Google Maps reverse geocoding
 * Web-only - uses navigator.geolocation and Google Maps Geocoder API
 *
 * @param {Object} options
 * @param {boolean} options.autoDetect - Auto-request geolocation on mount (default: true)
 * @param {number} options.timeout - Geolocation timeout in ms (default: 10000)
 * @returns {Object} { countryCode, loading, error, coordinates, retry }
 */
export const useUserGeolocation = (options = {}) => {
  const { autoDetect = true, timeout = 10000 } = options;

  const [state, setState] = useState({
    countryCode: null,
    loading: autoDetect,
    error: null,
    coordinates: null,
  });

  // Track if component is mounted to prevent setState after unmount
  const isMountedRef = useRef(true);

  // Safe setState that only updates if still mounted
  const safeSetState = useCallback((newState) => {
    if (isMountedRef.current) {
      setState(newState);
    }
  }, []);

  const detectLocation = useCallback(async () => {
    safeSetState(prev => ({ ...prev, loading: true, error: null }));

    // Check browser geolocation support
    if (!navigator.geolocation) {
      safeSetState({
        countryCode: null,
        loading: false,
        error: 'unsupported',
        coordinates: null,
      });
      return;
    }

    // Check if Google Maps API is loaded
    if (!window.google?.maps?.Geocoder) {
      safeSetState({
        countryCode: null,
        loading: false,
        error: 'maps_not_loaded',
        coordinates: null,
      });
      return;
    }

    try {
      // Get user's position from browser (relies on browser's built-in timeout)
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: false, // Faster, sufficient for country detection
            timeout: timeout,
            maximumAge: 300000, // Use cached position if available (5 min)
          }
        );
      });

      // Check if still mounted before continuing
      if (!isMountedRef.current) return;

      const { latitude, longitude } = position.coords;
      const coordinates = { lat: latitude, lng: longitude };

      // Reverse geocode using Google Maps API
      const geocoder = new window.google.maps.Geocoder();
      const geocodeResult = await new Promise((resolve, reject) => {
        geocoder.geocode(
          { location: { lat: latitude, lng: longitude } },
          (results, status) => {
            if (status === 'OK' && results?.[0]) {
              resolve(results[0]);
            } else {
              reject(new Error(`Geocoding failed: ${status}`));
            }
          }
        );
      });

      // Check if still mounted before setting state
      if (!isMountedRef.current) return;

      // Extract country code from address components
      const countryComponent = geocodeResult.address_components?.find(
        (component) => component.types.includes('country')
      );
      const detectedCode = countryComponent?.short_name;

      // Only return if country is in supported list
      if (detectedCode && ENABLED_COUNTRIES.includes(detectedCode)) {
        safeSetState({
          countryCode: detectedCode,
          loading: false,
          error: null,
          coordinates,
        });
      } else {
        // Country not supported
        safeSetState({
          countryCode: null,
          loading: false,
          error: 'unsupported_country',
          coordinates,
        });
      }
    } catch (err) {
      // Check if still mounted before setting error state
      if (!isMountedRef.current) return;

      let errorType = 'unknown';

      // Use standard GeolocationPositionError codes
      if (err.code === 1) {
        errorType = 'denied';
      } else if (err.code === 2) {
        errorType = 'unavailable';
      } else if (err.code === 3) {
        errorType = 'timeout';
      } else if (err.message?.includes('Geocoding')) {
        errorType = 'geocoding_failed';
      }

      safeSetState({
        countryCode: null,
        loading: false,
        error: errorType,
        coordinates: null,
      });
    }
  }, [timeout, safeSetState]);

  // Auto-detect on mount if enabled
  useEffect(() => {
    isMountedRef.current = true;

    if (autoDetect) {
      detectLocation();
    }

    // Cleanup: mark as unmounted to prevent memory leaks
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  return {
    ...state,
    retry: detectLocation,
  };
};

export default useUserGeolocation;
